/**
 * IP 地址獲取工具
 */

// 獲取用戶真實 IP 地址 (帶超時機制)
export const getUserIP = async () => {
  const timeout = 3000; // 3秒超時
  
  const fetchWithTimeout = async (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  try {
    // 方法 1: 使用 ipify.org API
    const response = await fetchWithTimeout('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('無法獲取外部 IP (ipify.org):', error.message);
    
    try {
      // 方法 2: 使用 ipapi.co (備用)
      const response = await fetchWithTimeout('https://ipapi.co/ip/');
      const ip = await response.text();
      return ip.trim();
    } catch (error2) {
      console.warn('所有 IP 獲取方法都失敗:', error2.message);
      return null;
    }
  }
};

// 創建帶有 IP 標頭的 axios 配置
export const createIPAwareConfig = async (baseConfig = {}) => {
  const userIP = await getUserIP();
  
  const config = {
    ...baseConfig,
    headers: {
      ...baseConfig.headers,
    }
  };
  
  if (userIP) {
    config.headers['X-Real-IP'] = userIP;
    config.headers['X-User-IP'] = userIP;
  }
  
  return config;
};

// 為 axios 實例添加 IP 攔截器
export const addIPInterceptor = (axiosInstance) => {
  let cachedIP = null;
  let ipPromise = null;
  let ipFetchAttempted = false;
  
  // 請求攔截器
  axiosInstance.interceptors.request.use(async (config) => {
    try {
      // 如果已經有快取的 IP，直接使用
      if (cachedIP) {
        config.headers['X-Real-IP'] = cachedIP;
        config.headers['X-User-IP'] = cachedIP;
        return config;
      }
      
      // 如果已經嘗試過獲取 IP 但失敗了，不再重試
      if (ipFetchAttempted && !ipPromise) {
        return config;
      }
      
      // 如果正在獲取 IP，設定超時等待
      if (ipPromise) {
        try {
          const ip = await Promise.race([
            ipPromise,
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('IP fetch timeout')), 2000)
            )
          ]);
          if (ip) {
            config.headers['X-Real-IP'] = ip;
            config.headers['X-User-IP'] = ip;
          }
        } catch (error) {
          console.warn('IP 獲取超時，繼續請求:', error.message);
        }
        return config;
      }
      
      // 第一次嘗試獲取 IP
      if (!ipFetchAttempted) {
        ipFetchAttempted = true;
        ipPromise = getUserIP();
        
        try {
          const ip = await Promise.race([
            ipPromise,
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Initial IP fetch timeout')), 2000)
            )
          ]);
          
          if (ip) {
            cachedIP = ip;
            config.headers['X-Real-IP'] = ip;
            config.headers['X-User-IP'] = ip;
            
            // 設定 5 分鐘後清除快取
            setTimeout(() => {
              cachedIP = null;
              ipPromise = null;
              ipFetchAttempted = false;
            }, 5 * 60 * 1000);
          }
        } catch (error) {
          console.warn('初次 IP 獲取失敗，繼續請求:', error.message);
          ipPromise = null;
        }
      }
      
      return config;
    } catch (error) {
      console.warn('請求攔截器錯誤:', error.message);
      return config;
    }
  });
  
  return axiosInstance;
};